import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin } from 'lucide-react';
import PhotoModal from '../../components/common/PhotoModal';
import { eventService } from '../../services/api';
import getDirectImageUrl from '../../utils/getDirectImageUrl';
import YouTubeEmbed from '../../components/common/YouTubeEmbed';

const PhotoGallery = ({ images, onPhotoClick }) => {
  const visibleImages = images.slice(0, 6);
  const remainingCount = Math.max(0, images.length - 6);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {visibleImages.map((image, index) => (
        <button
          key={index}
          onClick={() => onPhotoClick(index)}
          className="relative aspect-square rounded-lg overflow-hidden group"
        >
          <img
            src={getDirectImageUrl(image.url || image)}
            alt={image.alt || `Photo ${index + 1}`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              console.error(`Failed to load photo ${index + 1}`);
              e.target.src = '/default-event-image.jpg';
            }}
          />
          {index === 5 && remainingCount > 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white text-2xl font-bold">
                +{remainingCount}
              </span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);
        const eventData = await eventService.getEventById(eventId);
        
        // 完全移除视频和报告链接的自动重定向逻辑
        // 直接设置事件数据，让组件正常渲染
        setEvent(eventData);
        setError(null);
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Failed to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId, navigate]);

  const handlePhotoClick = (index) => {
    setCurrentPhotoIndex(index);
    setModalOpen(true);
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex(prev => 
      prev < (event?.pictures?.length || 0) - 1 ? prev + 1 : prev
    );
  };

  const handlePreviousPhoto = () => {
    setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : prev);
  };

  // Format time in a standardized way
  const formatTimeRange = (startTimeStr, endTimeStr) => {
    const startTime = new Date(startTimeStr);
    const endTime = new Date(endTimeStr);
    
    const startHour = startTime.getHours();
    const endHour = endTime.getHours();
    const startMinutes = startTime.getMinutes();
    const endMinutes = endTime.getMinutes();
    
    const startPeriod = startHour >= 12 ? 'PM' : 'AM';
    const endPeriod = endHour >= 12 ? 'PM' : 'AM';
    
    const formattedStartHour = (startHour % 12) || 12;
    const formattedEndHour = (endHour % 12) || 12;
    
    // If periods are the same, only show it once at the end
    if (startPeriod === endPeriod) {
      return `${formattedStartHour}:${startMinutes.toString().padStart(2, '0')} – ${formattedEndHour}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
    } else {
      return `${formattedStartHour}:${startMinutes.toString().padStart(2, '0')} ${startPeriod} – ${formattedEndHour}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FFD200] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#2C2C2C]">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error || 'Event not found'}</p>
          <Link 
            to="/events"
            className="mt-4 inline-block bg-[#FFD200] text-[#2C2C2C] px-6 py-2 rounded-lg hover:bg-[#FFE566]"
          >
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  // 提取所有非视频链接
  const nonVideoLinks = event.links?.additionalInfo?.filter(link => 
    !(link.url.includes('youtu.be/') || 
    link.url.includes('youtube.com/') || 
    link.title.toLowerCase().includes('video'))
  ) || [];

  // 提取报告链接
  const reportLinks = event.links?.additionalInfo?.filter(link => 
    link.title.toLowerCase().includes('report')
  ) || [];

  // 提取其他链接(不包括报告和视频)
  const otherLinks = nonVideoLinks.filter(link => 
    !link.title.toLowerCase().includes('report')
  );

  const formattedPhotos = event.pictures?.map((url, index) => ({
    url: getDirectImageUrl(url) || '/default-event-image.jpg',
    alt: `${event.title} - Photo ${index + 1}`,
    description: `Photo ${index + 1} of ${event.pictures.length}`
  })) || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header Section - Centered */}
      <div className="text-center mb-6">
        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="flex justify-center gap-4 mb-3">
            {event.tags.map(tag => (
              <span key={tag} className="bg-gray-100 px-4 py-1 rounded-full text-sm text-gray-600">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title and Subtitle */}
        <h1 className="text-4xl font-bold mb-2">{event.title}</h1>
        {event.subtitle && (
          <p className="text-xl text-gray-600 mb-4">{event.subtitle}</p>
        )}

        {/* Host Information */}
        {event.host && (
          <div className="flex items-center justify-center gap-3">
            <p className="text-gray-600">Hosted by</p>
            <div className="flex items-center gap-2">
              <div className="bg-[#FFD200] p-1 rounded-lg">
                <img
                  src={getDirectImageUrl(event.host.image) || '/default-host-image.jpg'}
                  alt={event.host.name}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.target.src = '/default-host-image.jpg';
                  }}
                />
              </div>
              <span className="font-medium text-[#2C2C2C]">
                {event.host.name}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Image */}
      {event.mainPicture && (
        <div className="mb-6 rounded-xl overflow-hidden max-h-[300px] max-w-xl mx-auto">
          <img
            src={getDirectImageUrl(event.mainPicture) || '/default-event-image.jpg'}
            alt={event.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/default-event-image.jpg';
            }}
          />
        </div>
      )}

      {/* YouTube Video - 新增部分 */}
      {event.video && (
        <div className="mb-8 max-w-xl mx-auto">
          <h2 className="text-2xl font-bold mb-3">Event Video</h2>
          <div className="youtube-embed-container">
            <YouTubeEmbed 
              url={event.video} 
              title={`${event.title} video`}
            />
          </div>
        </div>
      )}

      {/* Event Details */}
      <div className="prose max-w-xl mx-auto mb-8">
        {/* Date and Time */}
        <div className="flex flex-wrap justify-center gap-6 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#2C2C2C]" />
            <span className="text-lg">
              {new Date(event.startTime).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#2C2C2C]" />
            <span className="text-lg">
              {formatTimeRange(event.startTime, event.endTime)}
            </span>
          </div>
        </div>

        {/* Location */}
        {event.location && (
          <div className="flex justify-center items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#2C2C2C]" />
            <span className="text-lg">{event.location}</span>
          </div>
        )}

        {/* Event Links - 只显示非视频非报告链接 */}
        {otherLinks.length > 0 && (
          <div className="mb-4 text-center">
            <div className="flex flex-wrap justify-center gap-4">
              {otherLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors px-4 py-2"
                >
                  <span className="bg-blue-100 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </span>
                  {link.title}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* 报告链接 - 单独显示 */}
        {reportLinks.length > 0 && (
          <div className="mb-4 text-center">
            <div className="flex flex-wrap justify-center gap-4">
              {reportLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors px-4 py-2"
                >
                  <span className="bg-blue-100 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </span>
                  {link.title}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="whitespace-pre-line text-lg text-gray-700 leading-relaxed">
            {event.description}
          </div>
        )}

        {/* Registration Button */}
        {event.links?.registration && event.links.registration.url && (
          <div className="text-center mt-6">
            <a
              href={event.links.registration.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FFD200] text-[#2C2C2C] px-8 py-3 rounded-lg font-bold hover:bg-[#FFE566] transition-colors inline-block"
            >
              {event.links.registration.title || "Register Now"}
            </a>
          </div>
        )}
      </div>

      {/* Photo Album */}
      {event.pictures && event.pictures.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold">Photo Album</h2>
          <PhotoGallery 
            images={formattedPhotos} 
            onPhotoClick={handlePhotoClick}
          />
        </div>
      )}

      {/* Photo Modal */}
      <PhotoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentPhoto={formattedPhotos[currentPhotoIndex]}
        onNext={handleNextPhoto}
        onPrevious={handlePreviousPhoto}
        photos={formattedPhotos}
        currentIndex={currentPhotoIndex}
      />
    </div>
  );
};

export default EventDetailPage;